<!-- src/components/forms/completed/CossCompleted.vue -->
<template>
  <div class="space-y-12 animate-fadeIn">
    
    <!-- 1. Assessor & Candidate Identity -->
    <section>
      <div class="flex items-center gap-3 mb-6">
        <div class="bg-[#20FCF1] p-2 rounded-lg shadow-sm">
          <IdentificationIcon class="h-5 w-5 text-[#0A1630]" />
        </div>
        <h3 class="text-sm font-black uppercase tracking-[0.2em] text-[#0A1630]">Section 1: Personnel Identity</h3>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-6 bg-slate-50 p-8 rounded-[32px] border border-slate-100 shadow-inner">
        <div class="space-y-4">
          <p class="text-[10px] font-black uppercase text-slate-400 tracking-widest">Authorized Assessor</p>
          <div class="flex items-center gap-4 bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
            <div class="h-12 w-12 rounded-full bg-[#0A1630] flex items-center justify-center text-[#20FCF1] font-black">
              {{ props.submissionData.assessorName?.charAt(0) }}
            </div>
            <div>
              <p class="text-sm font-black text-[#0A1630] uppercase">{{ props.submissionData.assessorName }}</p>
              <p class="text-[10px] font-bold text-slate-400 uppercase tracking-tight">{{ formatText(props.submissionData.assessorJobTitle) }}</p>
            </div>
          </div>
        </div>

        <div class="space-y-4">
          <p class="text-[10px] font-black uppercase text-slate-400 tracking-widest">Candidate Credentials</p>
          <div class="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm space-y-2">
            <p class="text-sm"><strong>Name:</strong> <span class="font-bold text-[#0A1630] ml-1">{{ props.submissionData.candidateName }}</span></p>
            <p class="text-sm"><strong>Title/Grade:</strong> <span class="text-slate-600 ml-1">{{ formatText(props.submissionData.candidateJobTitle) }}</span></p>
            <p class="text-sm"><strong>Sentinel No:</strong> <span class="font-black text-brand-primary tracking-widest ml-1">{{ props.submissionData.candidateSentinelNumber }}</span></p>
            <p class="text-sm"><strong>Line Manager:</strong> <span class="text-slate-600 ml-1">{{ props.submissionData.candidateLineManager }}</span></p>
          </div>
        </div>
      </div>
    </section>

    <!-- 2. Assessment Parameters -->
    <section>
      <div class="flex items-center gap-3 mb-6">
        <div class="bg-[#20FCF1] p-2 rounded-lg shadow-sm">
          <CalendarDaysIcon class="h-5 w-5 text-[#0A1630]" />
        </div>
        <h3 class="text-sm font-black uppercase tracking-[0.2em] text-[#0A1630]">Section 2: Assessment Context</h3>
      </div>

      <!-- Parameter Metrics Grid (3 Columns) -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div class="StatCard">
          <span class="label">Assessed Date</span>
          <span class="value">{{ formatDate(props.submissionData.dateOfAssessment, 'dateOnly') }}</span>
        </div>
        <div class="StatCard">
          <span class="label">Reason</span>
          <span class="value truncate">{{ props.submissionData.reasonForAssessment === 'other' ? props.submissionData.reasonForAssessmentOther : formatText(props.submissionData.reasonForAssessment) }}</span>
        </div>
        <div class="StatCard">
          <span class="label">Channel</span>
          <span class="value">{{ formatText(props.submissionData.conversationChannel) }}</span>
        </div>
      </div>

      <!-- 🛡️ GEO-LOCATION STAMP DISPLAY CARD (Appears if geo-coordinates were captured upon submission) -->
      <div v-if="props.submissionData.geoCoords" class="mb-6 p-5 bg-white rounded-[24px] border border-slate-200 shadow-sm flex items-center justify-between gap-4">
        <div class="flex items-center gap-3">
          <div class="bg-[#0A1630] p-2.5 rounded-xl text-[#20FCF1]">
            <MapPinIcon class="h-5 w-5" />
          </div>
          <div>
            <p class="text-[9px] font-black uppercase text-slate-400 tracking-widest">Worksite Geo-Stamp Location</p>
            <p class="text-xs font-bold text-[#0A1630] font-mono mt-0.5">
              Lat: {{ props.submissionData.geoCoords.latitude.toFixed(6) }}, Long: {{ props.submissionData.geoCoords.longitude.toFixed(6) }}
            </p>
          </div>
        </div>
        <a 
          :href="`https://www.google.com/maps?q=${props.submissionData.geoCoords.latitude},${props.submissionData.geoCoords.longitude}`" 
          target="_blank" 
          rel="noopener noreferrer" 
          class="px-5 py-2.5 bg-[#0A1630] text-[#20FCF1] rounded-xl text-[9px] font-black uppercase tracking-widest hover:bg-[#20FCF1] hover:text-[#0A1630] transition-all shadow-sm flex items-center gap-2"
        >
          <span>View Map</span>
          <ArrowTopRightOnSquareIcon class="h-3.5 w-3.5" />
        </a>
      </div>

      <!-- Prominent Competence Hero Banner (Full Width) -->
      <div 
        :style="{ 
          backgroundColor: getCompetenceStyle(props.submissionData.competenceStandardAchieved).bgHex, 
          borderColor: getCompetenceStyle(props.submissionData.competenceStandardAchieved).borderHex,
          color: getCompetenceStyle(props.submissionData.competenceStandardAchieved).textHex
        }"
        class="border-2 rounded-[24px] p-6 shadow-md flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 transition-all"
      >
        <div class="text-left">
          <span 
            :style="{ color: getCompetenceStyle(props.submissionData.competenceStandardAchieved).labelHex }" 
            class="text-[10px] font-black uppercase tracking-[0.2em] block mb-1"
          >
            Competence Standard Achieved
          </span>
          <h4 class="text-xl font-black uppercase tracking-tight">
            {{ getCompetenceFullLabel(props.submissionData.competenceStandardAchieved) }}
          </h4>
        </div>
        <div class="shrink-0 flex items-center">
          <span 
            :style="{ 
              backgroundColor: getCompetenceStyle(props.submissionData.competenceStandardAchieved).badgeBgHex, 
              color: getCompetenceStyle(props.submissionData.competenceStandardAchieved).badgeTextHex 
            }"
            class="px-5 py-2.5 rounded-2xl text-[11px] font-black uppercase tracking-widest shadow-inner border border-white/10"
          >
            Grade {{ getGradeLetter(props.submissionData.competenceStandardAchieved) }}
          </span>
        </div>
      </div>
    </section>

    <!-- 3. Technical Checklists -->
    <section class="space-y-8">
      <div v-for="(section, idx) in cossChecklistSections" :key="section.id" 
           class="overflow-hidden border border-slate-200 rounded-[32px] shadow-sm bg-white">
        <div class="bg-slate-50 px-8 py-4 border-b border-slate-200 flex justify-between items-center">
          <h4 class="text-[10px] font-black uppercase tracking-[0.2em] text-[#0A1630]">Assessment Protocol {{ idx + 1 }}: {{ section.title }}</h4>
          <span class="bg-[#20FCF1]/10 text-[#0A1630] px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-tighter">Review Pass</span>
        </div>
        <div class="p-8 space-y-4">
          <div v-for="q in section.questions" :key="q.id" class="flex items-start justify-between gap-6 pb-4 border-b border-slate-50 last:border-0 last:pb-0">
            <p class="text-xs font-bold text-slate-600 leading-relaxed">{{ q.text }}</p>
            <div class="flex-shrink-0">
              <span :class="getStatusClass(props.submissionData[q.modelKey])" class="px-4 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest shadow-sm">
                {{ formatYesNoNA(props.submissionData[q.modelKey]) }}
              </span>
            </div>
          </div>
          
          <div v-if="props.submissionData[section.commentsModelKey]" class="mt-6 p-4 bg-slate-50 rounded-2xl border-l-4 border-[#20FCF1]">
            <p class="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-1">Assessor Comments</p>
            <p class="text-xs text-slate-700 italic">{{ props.submissionData[section.commentsModelKey] }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- 4. Action Plan -->
    <section v-if="showActionPlan" class="bg-[#0A1630] p-10 rounded-[40px] text-white shadow-2xl relative overflow-hidden">
      <div class="absolute top-0 right-0 p-10 opacity-5">
        <ArrowTrendingUpIcon class="h-32 w-32" />
      </div>
      <div class="relative z-10 space-y-8">
        <div class="flex items-center gap-3">
          <div class="bg-[#20FCF1] p-2 rounded-lg">
            <ClipboardDocumentCheckIcon class="h-5 w-5 text-[#0A1630]" />
          </div>
          <h3 class="text-sm font-black uppercase tracking-[0.2em] text-[#20FCF1]">Development Action Plan</h3>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div class="bg-white/5 p-6 rounded-[24px] border border-white/10">
            <p class="text-[9px] font-black uppercase text-[#20FCF1] tracking-widest mb-2">Development Need</p>
            <p class="text-sm font-medium">{{ props.submissionData.devNeed }}</p>
          </div>
          <div class="bg-white/5 p-6 rounded-[24px] border border-white/10">
            <p class="text-[9px] font-black uppercase text-[#20FCF1] tracking-widest mb-2">Closure Strategy</p>
            <p class="text-sm font-medium">{{ props.submissionData.devPlanClosedHow }}</p>
          </div>
        </div>

        <div class="grid grid-cols-2 md:grid-cols-4 gap-4 pt-6 border-t border-white/10">
           <div class="PlanMetric"><span>Agreed Date</span><strong>{{ formatDate(props.submissionData.devDatePlanAgreed, 'dateOnly') }}</strong></div>
           <div class="PlanMetric"><span>Interim Review</span><strong>{{ formatDate(props.submissionData.devInterimReviewDate, 'dateOnly') }}</strong></div>
           <div class="PlanMetric"><span>Target Date</span><strong>{{ formatDate(props.submissionData.devTargetDateCompletion, 'dateOnly') }}</strong></div>
           <div class="PlanMetric"><span>Actual Closure</span><strong>{{ formatDate(props.submissionData.devActualDateClosed, 'dateOnly') }}</strong></div>
        </div>

        <div class="bg-[#20FCF1] p-6 rounded-[24px] text-[#0A1630]">
          <p class="text-[9px] font-black uppercase tracking-widest mb-2 opacity-60">Closure Sign-Off By</p>
          <p class="text-lg font-black uppercase tracking-tight">{{ props.submissionData.devClosedBy || 'N/A' }}</p>
        </div>
      </div>
    </section>

    <!-- 5. Certification Sign-off -->
    <section class="border-t pt-12">
      <div class="flex flex-col md:flex-row justify-between items-end gap-8">
        <div class="space-y-2">
          <p class="text-[10px] font-black uppercase text-slate-400 tracking-[0.2em]">Official Record Reference</p>
          <p class="text-xl font-black text-[#0A1630] tracking-tighter">{{ props.submissionData.humanReadableId || props.submissionData.referenceNumber }}</p>
          <!-- 🛡️ FIXED TIMESTAMP RESOLUTION: Direct fallback to submittedAt -->
          <p class="text-xs text-slate-500">Certified on {{ formatDate(props.submissionData.submittedAt || props.submissionData.signatureDate) }}</p>
        </div>

        <div v-if="props.submissionData.signatureDataUrl" class="bg-white p-6 rounded-[32px] border border-slate-200 shadow-xl flex flex-col items-center min-w-[240px]">
          <img :src="props.submissionData.signatureDataUrl" alt="Assessor Seal" class="max-h-20 object-contain mb-4" />
          <div class="w-full h-px bg-slate-100 mb-4"></div>
          <div class="flex items-center gap-2">
            <CheckBadgeIcon class="h-4 w-4 text-[#20FCF1]" />
            <span class="text-[10px] font-black uppercase text-[#0A1630] tracking-widest">Assessor Attestation Verified</span>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { 
  IdentificationIcon, CalendarDaysIcon, CheckBadgeIcon, 
  ArrowTrendingUpIcon, ClipboardDocumentCheckIcon,
  MapPinIcon, ArrowTopRightOnSquareIcon
} from '@heroicons/vue/24/outline';

const props = defineProps({
  submissionData: { type: Object, required: true },
  formatDate: { type: Function, required: true },
  formatText: { type: Function, required: true },
  formatYesNoNA: { type: Function, required: true },
  getStatusClass: { type: Function, required: true }
});

const getGradeLetter = (val) => {
  if (!val) return 'N/A';
  const char = val.toString().trim().charAt(0).toUpperCase();
  return ['A', 'B', 'C', 'D'].includes(char) ? char : 'N/A';
};

const getCompetenceFullLabel = (val) => {
  if (!val) return 'N/A';
  const normalized = val.toString().trim().toUpperCase();
  if (normalized.startsWith('A')) return 'A - Fully Competent';
  if (normalized.startsWith('B')) return 'B - Competent with Development';
  if (normalized.startsWith('C')) return 'C - Requires Action';
  if (normalized.startsWith('D')) return 'D - Requires Immediate Action';
  return val;
};

const getCompetenceStyle = (val) => {
  if (!val) {
    return {
      bgHex: '#F8FAFC',
      borderHex: '#E2E8F0',
      textHex: '#0A1630',
      labelHex: '#64748B',
      badgeBgHex: '#E2E8F0',
      badgeTextHex: '#0A1630'
    };
  }
  const char = val.toString().trim().charAt(0).toUpperCase();
  
  if (char === 'A') {
    return {
      bgHex: '#EBFDF8',
      borderHex: '#20FCF1',
      textHex: '#0A1630',
      labelHex: '#047857',
      badgeBgHex: '#20FCF1',
      badgeTextHex: '#0A1630'
    };
  }
  if (char === 'B') {
    return {
      bgHex: '#FEF9C3',
      borderHex: '#FDE047',
      textHex: '#713F12',
      labelHex: '#A16207',
      badgeBgHex: '#FDE047',
      badgeTextHex: '#713F12'
    };
  }
  if (char === 'C') {
    return {
      bgHex: '#FFEDD5',
      borderHex: '#FDBA74',
      textHex: '#7C2D12',
      labelHex: '#C2410C',
      badgeBgHex: '#FDBA74',
      badgeTextHex: '#7C2D12'
    };
  }
  if (char === 'D') {
    return {
      bgHex: '#FFE4E6',
      borderHex: '#FCA5A5',
      textHex: '#881337',
      labelHex: '#BE123C',
      badgeBgHex: '#FCA5A5',
      badgeTextHex: '#881337'
    };
  }
  
  return {
    bgHex: '#F8FAFC',
    borderHex: '#E2E8F0',
    textHex: '#0A1630',
    labelHex: '#64748B',
    badgeBgHex: '#E2E8F0',
    badgeTextHex: '#0A1630'
  };
};

const showActionPlan = computed(() => {
  const std = props.submissionData?.competenceStandardAchieved || '';
  if (std.toUpperCase().startsWith('A')) return false;
  return !!(props.submissionData?.devNeed || props.submissionData?.devPlanClosedHow);
});

const cossChecklistSections = [
    { id: 'identity', title: 'Identity', commentsModelKey: 'identityComments', questions: [ 
        { id: 'id_name', text: 'Individual fully identified themselves and other parties by name', modelKey: 'id_name' }, 
        { id: 'id_role', text: 'Individual identified role and company where appropriate', modelKey: 'id_roleCompany' }, 
        { id: 'id_location', text: 'Individual identified location from which they were speaking', modelKey: 'id_locationSpeaking' }, 
        { id: 'id_prompt', text: 'Prompted other party if they did not identify identity/location', modelKey: 'id_promptIfNotIdentified' }, 
        { id: 'id_repeat', text: 'Repeated back to confirm correct person and location understanding', modelKey: 'id_repeatConfirmCorrectPerson' }
    ]},
    { id: 'delivery', title: 'Message Content (Delivery)', commentsModelKey: 'deliveryComments', questions: [ 
        { id: 'del_intention', text: 'Clearly stated intention/purpose of the communication', modelKey: 'del_clearIntention' }, 
        { id: 'del_logical', text: 'Delivered message in a structured, logical manner', modelKey: 'del_logicalStructure' }, 
        { id: 'del_concise', text: 'Message was concise without superfluous chat', modelKey: 'del_conciseNoChat' }, 
        { id: 'del_accurate', text: 'Delivered information accurately', modelKey: 'del_accurateInfo' }, 
        { id: 'del_phonetic', text: 'Used phonetic alphabet appropriately for technical data', modelKey: 'del_phoneticAlphabet' },
        { id: 'del_24hr', text: 'Conveyed times using the 24hr clock format', modelKey: 'del_24hrClock' }
    ]},
    { id: 'receiving', title: 'Message Content (Receiving)', commentsModelKey: 'receivingComments', questions: [ 
        { id: 'rec_listen', text: 'Listens intently to provided information', modelKey: 'rec_listenInfo' }, 
        { id: 'rec_clarify', text: 'Asked pertinent questions to clarify understanding', modelKey: 'rec_askClarify' }, 
        { id: 'rec_persist', text: 'Persisted with questioning until information was verified', modelKey: 'rec_persistQuestioning' }
    ]},
    { id: 'understanding', title: 'Confirming Understanding', commentsModelKey: 'understandingComments', questions: [ 
        { id: 'und_repeat', text: 'Repeated back or summarized all critical details', modelKey: 'und_repeatSummarise' }, 
        { id: 'und_correct', text: 'Identified and corrected errors in the repeat-back', modelKey: 'und_correctErrorsInRepeat' }
    ]},
    { id: 'overall', title: 'Overall Performance', commentsModelKey: 'overallComments', questions: [ 
        { id: 'over_tone', text: 'Maintained a confident tone of voice throughout', modelKey: 'over_confidentTone' }, 
        { id: 'over_pro', text: 'Remained professional and polite during the exchange', modelKey: 'over_professionalPolite' }, 
        { id: 'over_msg', text: 'Successfully conveyed and received an accurate message', modelKey: 'over_accurateMessage' }
    ]}
];
</script>

<style scoped>
.StatCard { @apply flex flex-col p-5 bg-white border border-slate-200 rounded-[24px] shadow-sm; }
.StatCard .label { @apply text-[9px] font-black uppercase text-slate-400 tracking-widest mb-1; }
.StatCard .value { @apply text-sm font-black text-[#0A1630] truncate; }
.PlanMetric { @apply flex flex-col; }
.PlanMetric span { @apply text-[9px] font-black uppercase text-white/40 tracking-widest; }
.PlanMetric strong { @apply text-xs font-bold text-white; }
.animate-fadeIn { animation: fadeIn 0.4s ease-out forwards; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
</style>